/**
 * Plan 07 — skeletal prototype: the thin Babylon adapter over the pure clip library.
 *
 * Responsibilities (rigged-asset contract: "one asset-specific loader/factory with disposal"):
 *   * buildSkeleton — bones at the rig's bind positions (local translation = child − parent), so
 *     the inverse-bind matrices reproduce the authored stance exactly (rigid binding ⇒ a freshly
 *     bound mesh renders identical to the pre-skeletal merged mesh);
 *   * bindRigidSkin — one bone per merged part (vertex ranges from buildSoldier/buildBoss), weight
 *     1.0, inside the ≤4-influence contract;
 *   * applyPose — pure Pose ⇒ bone local transforms (setRotationQuaternion LOCAL + setPosition
 *     LOCAL; no animatables, no clock — the whole pose is re-derived from the snapshot each frame).
 *
 * No Babylon Animation/AnimationGroup runtime is used on purpose: playback phase comes from sim
 * state (P5.3 purity), so pause freezes the crowd exactly and resumed screenshots reproduce.
 */
import { Bone, Matrix, Quaternion, Skeleton, Space, Vector3 } from '@babylonjs/core/pure';
import type { Mesh, Scene } from '@babylonjs/core/pure';
import type { Rig } from './rigs';
import type { Pose } from './clips';

export interface SkeletonHandle {
  skeleton: Skeleton;
  /** Bones in rig order (index matches Pose channels). */
  bones: Bone[];
  /** Bind LOCAL positions (parent-relative), for pose application. */
  bindLocal: Float32Array;
  rigId: string;
}

export function buildSkeleton(scene: Scene, rig: Rig, name: string): SkeletonHandle {
  const skeleton = new Skeleton(`${name}-skeleton`, `${name}-skeleton-id`, scene);
  const bones: Bone[] = [];
  const bindLocal = new Float32Array(rig.bones.length * 3);
  const modelPos = (i: number): [number, number, number] => [rig.bones[i]!.pos[0], rig.bones[i]!.pos[1], rig.bones[i]!.pos[2]];
  rig.bones.forEach((bone, i) => {
    const [mx, my, mz] = modelPos(i);
    let lx = mx;
    let ly = my;
    let lz = mz;
    if (bone.parent !== null) {
      const p = rig.bones.findIndex((b) => b.name === bone.parent);
      if (p < 0) throw new Error(`[skeletal] rig ${rig.id}: bone ${bone.name} has unknown parent ${bone.parent}`);
      const [px, py, pz] = modelPos(p);
      lx -= px;
      ly -= py;
      lz -= pz;
    }
    bindLocal.set([lx, ly, lz], i * 3);
    const local = Matrix.Translation(lx, ly, lz);
    const parentBone = bone.parent === null ? null : bones[rig.bones.findIndex((b) => b.name === bone.parent)]!;
    bones.push(new Bone(bone.name, skeleton, parentBone, local));
  });
  return { skeleton, bones, bindLocal, rigId: rig.id };
}

/**
 * Rigid skin binding: every merged part is bound whole to its bone (weight 1.0). `parts` supplies
 * the vertex ranges (MergeMeshes concatenates in source order — plan 05 P5.2), `partBone` maps a
 * part name to its bone name. Returns the bone-index histogram for tests/probes.
 */
export function bindRigidSkin(
  mesh: Mesh,
  handle: SkeletonHandle,
  parts: { name: string; vertexStart: number; vertexEnd: number }[],
  rig: Rig,
  partBone: (part: string) => string,
): { boneIndices: number[]; boneNames: string[] } {
  const index = new Map(rig.bones.map((b, i) => [b.name, i] as const));
  const vertexCount = mesh.getTotalVertices();
  const indices = new Float32Array(vertexCount * 4);
  const weights = new Float32Array(vertexCount * 4);
  const boneIndices: number[] = [];
  const boneNames: string[] = [];
  const byName = new Map(parts.map((p) => [p.name, p] as const));
  if (byName.size !== parts.length) throw new Error('[skeletal] duplicate part names in geometry');
  for (const part of parts) {
    const boneIndex = index.get(partBone(part.name));
    if (boneIndex === undefined) throw new Error(`[skeletal] part "${part.name}" has no bone in rig ${rig.id}`);
    if (part.vertexEnd > vertexCount) throw new Error(`[skeletal] part "${part.name}" range exceeds vertex count`);
    for (let v = part.vertexStart; v < part.vertexEnd; v++) {
      indices[v * 4] = boneIndex;
      weights[v * 4] = 1;
    }
    boneIndices.push(boneIndex);
    boneNames.push(rig.bones[boneIndex]!.name);
  }
  mesh.setVerticesData('matricesIndices', indices, false);
  mesh.setVerticesData('matricesWeights', weights, false);
  mesh.skeleton = handle.skeleton;
  return { boneIndices, boneNames };
}

const tmpQuat = new Quaternion();
const tmpVec = new Vector3();
const tmpOne = new Vector3(1, 1, 1);

/**
 * Pure Pose ⇒ bone local transforms. Deterministic — bit-exact under repetition: the Babylon
 * decompose/compose roundtrip re-derives scale as 1±ε from the previous frame’s matrix, so the
 * scale is forced back to exactly 1 (setScale composes from parts, it does not decompose) and the
 * translation is overwritten afterwards; identical snapshots therefore produce identical matrices
 * no matter what pose the bone carried in (P5.3 for bones — pinned in squad/boss tests).
 */
export function applyPose(handle: SkeletonHandle, pose: Pose): void {
  const { bones, bindLocal } = handle;
  for (let i = 0; i < bones.length; i++) {
    const o = i * 3;
    const rx = pose.rot[o]!;
    const ry = pose.rot[o + 1]!;
    const rz = pose.rot[o + 2]!;
    Quaternion.RotationYawPitchRollToRef(ry, rx, rz, tmpQuat);
    bones[i]!.setRotationQuaternion(tmpQuat, Space.LOCAL);
    bones[i]!.setScale(tmpOne); // kills the roundtrip’s 1±ε scale — see header
    tmpVec.set(bindLocal[o]! + pose.pos[o]!, bindLocal[o + 1]! + pose.pos[o + 1]!, bindLocal[o + 2]! + pose.pos[o + 2]!);
    bones[i]!.setPosition(tmpVec, Space.LOCAL);
  }
}
